import type { StateMachine } from './machine';

/** Visit state machine — core-data-model §3.2. THE offline conflict policy for tech
 *  status transitions: a replayed visit.complete against a canceled visit is illegal. */
export const VISIT_STATES = [
  'scheduled',
  'en_route',
  'in_progress',
  'completed',
  'canceled',
] as const;
export type VisitState = (typeof VISIT_STATES)[number];

export const visitMachine: StateMachine<VisitState> = {
  states: VISIT_STATES,
  initial: 'scheduled',
  terminal: ['completed', 'canceled'],
  transitions: [
    { from: 'scheduled', to: 'en_route', via: 'visit.enRoute' },
    { from: 'scheduled', to: 'in_progress', via: 'visit.start' }, // skip en_route allowed
    { from: 'en_route', to: 'in_progress', via: 'visit.start' },
    { from: 'en_route', to: 'scheduled', via: 'visit.abort' }, // turn back / reschedule
    { from: 'in_progress', to: 'completed', via: 'visit.complete' },
    { from: 'scheduled', to: 'canceled', via: 'visit.cancel' },
    { from: 'en_route', to: 'canceled', via: 'visit.cancel' },
  ],
};

/**
 * The shop-local calendar days after its check-out day that a done visit stays readable to the
 * tech on it (CRM-14, Default (R9 Q8) #37): checked out on a Monday, readable through the next
 * Monday. The server enforces it (`apps/api/src/services/visit-window.ts`); the tech's screens
 * name it.
 */
export const VISIT_WINDOW_DAYS = 7;

/** Mutations that stamp actuals on the visit (service layer sets the timestamp). */
export const VISIT_STAMPS = {
  'visit.start': 'actual_start_at',
  'visit.complete': 'actual_end_at',
} as const;

/**
 * Why a visit can't be moved — in the words the office would use, or null if it can.
 *
 * Rescheduling = editing scheduled_start_at on a SCHEDULED visit; it is an event, not a
 * state. Once a tech is rolling, the day has overtaken the plan and moving the row would
 * rewrite history rather than change it.
 *
 * Lives in shared so the board can GREY OUT what it cannot move instead of offering a
 * button that is guaranteed to come back refused. One rule, both ends — the alternative is
 * the client keeping its own copy of the server's mind and the two drifting apart.
 *
 * `Record<…>` on purpose: a sixth visit state breaks this build instead of silently
 * inheriting "movable" and letting a bulk move rewrite a stop that is already underway.
 */
const NOT_RESCHEDULABLE: Record<Exclude<VisitState, 'scheduled'>, string> = {
  en_route: 'a tech is already on the way',
  in_progress: 'a tech is already on site',
  completed: 'the work is already finished',
  canceled: 'it was canceled',
};

export function whyNotReschedulable(status: string): string | null {
  return status === 'scheduled'
    ? null
    : (NOT_RESCHEDULABLE[status as Exclude<VisitState, 'scheduled'>] ??
        `its status is ${status}`);
}

/**
 * Why the people on a visit can't be changed — or null if they can.
 *
 * ⚠️ A LOOSER rule than rescheduling, deliberately, because it answers a different
 * question. Moving a visit that is underway rewrites the plan after the day has overtaken
 * it. Changing who is on one that is underway is an ordinary thing a dispatcher does: a
 * tech calls in sick on the way, or a second pair of hands is needed on site. So
 * `en_route` and `in_progress` stay open here while they are closed above.
 *
 * What is closed is TERMINAL. `visit_assignments` on a finished visit is the record of who
 * did the work; editing it is not dispatch, it is falsifying a timesheet.
 *
 * ⚠️ Being reassignable does NOT mean anyone may be REMOVED — see `whyCannotDropAssignee`.
 *
 * `Record<…>` on purpose, same as above: a sixth visit state breaks this build rather than
 * silently inheriting "editable".
 */
const NOT_REASSIGNABLE: Record<'completed' | 'canceled', string> = {
  completed: 'the work is already finished',
  canceled: 'it was canceled',
};

export function whyNotReassignable(status: string): string | null {
  return NOT_REASSIGNABLE[status as 'completed' | 'canceled'] ?? null;
}

/**
 * Why an existing assignee can't be taken OFF this visit — or null if they can.
 *
 * 🔴 The hazard this exists for: a tech's access to a job's data is scoped on their LIVE
 * assignment row (`mcp/reads/_util.ts` — "an un-assigned (soft-deleted) tech loses
 * access"). Dropping someone from a visit they have already rolled on therefore revokes
 * their access to the job **while they are standing in the customer's basement**, and the
 * offline write queue they have been filling can no longer replay against a visit they may
 * not read. Adding help is safe; quietly removing the person doing the work is not.
 *
 * So once a visit leaves `scheduled`, the set may only GROW. To take someone off a visit
 * that is underway, send it back with `visit.abort` first — an explicit, recorded step.
 */
export function whyCannotDropAssignee(status: string): string | null {
  return status === 'scheduled'
    ? null
    : 'someone is already on their way to this one — send it back first, then change who goes';
}
